import React from 'react';
import classNames from 'classnames';
import { useRouter } from 'next/router';
import { z } from 'zod';
import type { ContentComponent } from '$cms/ContentComponent';
import { CmsLinkSchema, CmsRichTextSchema, CmsShortTextSchema } from '$cms/types';
import { HeadingLevels } from '$src-components/atoms/HeadingLevels';
import type { SchemaType } from '$util/schema';
import { renderRichTextContent } from '@/cms/richTextContentHelpers';
import { Grid, Row, Col } from '@/components/atoms/Grid';
import { Button, DesignType } from '@/components/atoms/Button/';

import styles from './index.module.scss';

const RichTextSectionSchema = {
  translatedEntities: z.object({
    heading: CmsShortTextSchema.optional(),
    subheading: CmsShortTextSchema.optional(),
    content: CmsRichTextSchema.optional(),
    primaryCTA: CmsLinkSchema.optional(),
    secondaryCTA: CmsLinkSchema.optional(),
  }),
};

export type RichTextSectionTranslatedEntities = SchemaType<
  (typeof RichTextSectionSchema)['translatedEntities']
>;

/**
 * Section dialog which render content from Contenful <br />
 * https://www.notion.so/fixter/RichTextSection-ef58c307c15944a18c780c078c6c7279
 */
export const RichTextSection: ContentComponent<RichTextSectionTranslatedEntities> = function RichTextSection({
  className,
  translatedEntities,
  ...props
}): JSX.Element {
  const { primaryCTA, secondaryCTA, content, heading, subheading } = translatedEntities;
  const { push } = useRouter();

  const richTextContent = content && renderRichTextContent(content.content);

  return (
    <Grid className={classNames(RichTextSection.displayName, styles.component, className)} {...props}>
      <Row>
        <Col extraSmall={4} small={4} smallStart={2} medium={6} mediumStart={4}>
          {heading && (
            <HeadingLevels semanticLevel={2} className={styles.Heading}>
              {heading.value}
            </HeadingLevels>
          )}
          {subheading && (
            <HeadingLevels semanticLevel={4} className={styles.Subheading}>
              {subheading.value}
            </HeadingLevels>
          )}
          {richTextContent && <div className={styles.content}>{richTextContent}</div>}
          {(primaryCTA || secondaryCTA) && (
            <div className={styles.cta}>
              {primaryCTA && (
                <Button size="S" onClick={() => push(primaryCTA.url)}>
                  {primaryCTA.text}
                </Button>
              )}
              {secondaryCTA && (
                <Button
                  size="S"
                  onClick={() => push(secondaryCTA.url)}
                  designType={DesignType.SECONDARY_DARK}
                >
                  {secondaryCTA.text}
                </Button>
              )}
            </div>
          )}
        </Col>
      </Row>
    </Grid>
  );
};
RichTextSection.displayName = 'RichTextSection';
RichTextSection.schema = RichTextSectionSchema;
